import type {OrganizationADto, SsoProviderADto} from '@cohort/admin-schemas/organization';
import Button from '@cohort/merchants/components/buttons/Button';
import {InputWithLabel} from '@cohort/merchants/components/form/InputWithLabel';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {notify} from '@cohort/merchants/hooks/toast';
import {useIsMounted} from '@cohort/merchants/hooks/useIsMounted';
import {sendSigninOtp} from '@cohort/merchants/lib/api/Auth';
import {loginWithSsoProvider, ORG_PICKER_TENANT_ID} from '@cohort/merchants/lib/Firebase';
import {
  trackError,
  trackSigninError,
  trackSigninFormRedirect,
  trackSigninSendLinkSuccess,
  trackSigninSuccess,
} from '@cohort/merchants/lib/Tracking';
import {waitlistFormRedirect} from '@cohort/merchants/lib/Utils';
import OrganizationLogo from '@cohort/merchants/pages/sign-in/OrganizationLogo';
import {notifyError} from '@cohort/merchants/stores/ErrorModalStore';
import {SSO_GOOGLE_DEFAULT_PROVIDER} from '@cohort/shared/constants';
import {EmailSchema} from '@cohort/shared/schema/common';
import {getImageUrl, Sizes} from '@cohort/shared/utils/media';
import {isCohortError} from '@cohort/shared-frontend/common/errors';
import {useTurnstile} from '@cohort/shared-frontend/hooks/useTurnstile';
import {cn} from '@cohort/shared-frontend/utils/classNames';
import {zodResolver} from '@hookform/resolvers/zod';
import {CaretLeft} from '@phosphor-icons/react';
import React, {Fragment, useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {z} from 'zod';

const RESEND_CODE_DELAY_SEC = 15;

const SendSigninOtpFormSchema = z.object({email: EmailSchema});

type SignInFormProps = {
  organization: OrganizationADto | null;
  onUserLoggedIn: () => void;
};

const SignInForm: React.FC<SignInFormProps> = ({organization, onUserLoggedIn}) => {
  const [checkInboxPanelOpened, setCheckInboxPanelOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useIsMounted();
  const {t} = useTranslation('pages', {keyPrefix: 'sign-in.signInForm'});
  const [timeLeft, setTimeLeft] = useState(0);
  const {token, resetToken} = useTurnstile();

  const emailEnabled = organization === null || organization.adminEmailSigninEnabled;
  const ssoProvider: SsoProviderADto | null = organization
    ? organization.adminSsoProvider
    : SSO_GOOGLE_DEFAULT_PROVIDER;

  const {
    register,
    handleSubmit,
    getValues,
    formState: {errors},
  } = useForm({
    defaultValues: {
      email: '',
    },
    mode: 'onBlur',
    resolver: zodResolver(SendSigninOtpFormSchema),
  });

  const {mutate: handleEmailLinkSignIn} = useCohortMutation({
    mutationFn: async ({email}: {email: string}) => {
      if (!token) {
        return;
      }
      const res = await sendSigninOtp(email, token);
      return res;
    },
    onMutate: () => setIsLoading(true),
    onSuccess: async () => {
      if (checkInboxPanelOpened) {
        notify('success', t('resendSuccess'));
      }
      resetToken?.();
      setTimeLeft(RESEND_CODE_DELAY_SEC);
      setCheckInboxPanelOpened(true);
      trackSigninSendLinkSuccess();
    },
    onError: err => {
      if (isCohortError('organization-member.not-found')) {
        trackSigninFormRedirect({signinMethod: 'emailLink'});
        waitlistFormRedirect();
      } else {
        trackSigninError({signinMethod: 'emailLink'});
        trackError(err);
      }
    },
    onSettled: () => {
      if (isMounted()) {
        setIsLoading(false);
      }
    },
  });

  const {mutate: handleSsoSignIn} = useCohortMutation({
    mutationFn: async ({tenantId, ssoProvider}: {ssoProvider: SsoProviderADto; tenantId: string}) =>
      loginWithSsoProvider(tenantId, ssoProvider.type, ssoProvider.providerId),
    onSuccess: async () => {
      trackSigninSuccess({signinMethod: 'saml'});
      onUserLoggedIn();
    },
    onError: err => {
      if (typeof err === 'object' && err !== null && 'code' in err) {
        if (
          err.code === 'auth/popup-closed-by-user' ||
          err.code === 'auth/cancelled-popup-request'
        ) {
          // Fail silently if the user closes the popup
          return;
        }
      } else {
        trackSigninError({signinMethod: 'saml'});
        notifyError(err);
      }
    },
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (timeLeft > 0) {
        setTimeLeft(timeLeft - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  if (checkInboxPanelOpened) {
    return (
      <div className="flex flex-col">
        <Button
          variant={'link'}
          className={cn('-ml-1 justify-start p-0', organization !== null && 'mb-4')}
          onClick={() => setCheckInboxPanelOpened(false)}
        >
          <CaretLeft className="h-5 w-5" />
          {t('back')}
        </Button>
        {organization !== null && <OrganizationLogo organization={organization} />}
        <h1 className="py-6 text-2xl font-semibold">{t('checkEmail')}</h1>
        <p className="text-md text-leading-5 text-sm text-slate-500">
          <Trans
            i18nKey="sign-in.signInForm.linkSentTo"
            ns="pages"
            components={{
              bold: <span className="font-semibold" />,
            }}
            values={{email: getValues('email')}}
          />
        </p>
        <p className="text-md text-leading-5 text-sm text-slate-500">{t('linkInstructions')}</p>
        <Button
          onClick={handleSubmit(data => handleEmailLinkSignIn(data))}
          variant="secondary"
          className="mt-6 h-11 w-full"
          loading={isLoading}
          disabled={timeLeft > 0}
        >
          {timeLeft > 0 ? t('emailResendTimer', {numSeconds: timeLeft}) : t('resendEmail')}
        </Button>
      </div>
    );
  }

  return (
    <Fragment>
      {organization !== null && <OrganizationLogo organization={organization} />}
      <h1 className="py-6 text-2xl font-semibold">
        {organization !== null
          ? t('title', {
              organizationName: organization.name,
            })
          : t('titleNoOrg')}
      </h1>
      <div className="flex flex-col space-y-4">
        {emailEnabled && (
          <form
            onSubmit={handleSubmit(data => handleEmailLinkSignIn(data))}
            className="flex flex-col space-y-4"
          >
            <InputWithLabel
              label={t('emailAddress')}
              error={errors.email?.message}
              placeholder="vitalik@getcohort.com"
              type="text"
              {...register('email')}
              testId="email"
            />
            <Button
              type="submit"
              className="h-11 w-full"
              loading={isLoading}
              data-testid="get-started"
              disabled={timeLeft > 0}
            >
              {timeLeft > 0 ? t('emailResendTimer', {numSeconds: timeLeft}) : t('buttonContinue')}
            </Button>
            {ssoProvider && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-slate-500">{t('dividerOr')}</span>
                </div>
              </div>
            )}
          </form>
        )}

        {ssoProvider && (
          <div>
            <div>
              <Button
                variant="secondary"
                className="h-11 w-full"
                onClick={() => {
                  const tenantId = organization
                    ? organization.adminAuthTenantId
                    : ORG_PICKER_TENANT_ID;
                  handleSsoSignIn({ssoProvider, tenantId});
                }}
              >
                {ssoProvider.logoFileKey && (
                  <img
                    className="mr-3 h-6 w-6"
                    src={getImageUrl(import.meta.env.COHORT_ENV, ssoProvider.logoFileKey, {
                      h: Sizes.XS,
                      w: Sizes.XS,
                      quality: '100',
                    })}
                  />
                )}
                {t('signInWith', {provider: ssoProvider.name})}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default SignInForm;
